import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaLock,
  FaEye,
  FaEyeSlash,
  FaCheckCircle,
  FaTimesCircle,
  FaShieldAlt,
} from "react-icons/fa";
import SeoMeta from "../SeoMeta";
import { useToastHelpers } from "../Toasts/ToastUtils";
import { resetPassword } from "../../apis/UserApis";

const MIN_PASSWORD_LEN = 8;

export default function ResetPasswordPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { successToast, errorToast } = useToastHelpers();

  const token = useMemo(() => {
    try {
      return new URLSearchParams(location.search).get("token") || "";
    } catch {
      return "";
    }
  }, [location.search]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordRules = {
    upperCase: /[A-Z]/.test(newPassword),
    lowerCase: /[a-z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword),
    minLength: newPassword.length >= MIN_PASSWORD_LEN,
  };

  const passwordOk = Object.values(passwordRules).every(Boolean);
  const confirmOk = confirmPassword.length === 0 || confirmPassword === newPassword;
  const canSubmit = Boolean(token) && passwordOk && confirmPassword === newPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      errorToast("Missing reset token");
      return;
    }

    if (!passwordOk) {
      errorToast("Password must meet all requirements");
      return;
    }

    if (confirmPassword !== newPassword) {
      errorToast("Passwords do not match");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword({ token, newPassword });
      successToast("Password updated", "You can now login with your new password");
      navigate("/login");
    } catch (err) {
      console.error("Reset password failed:", err);
      const status = err?.response?.status;

      if (status === 401) {
        errorToast("Invalid or expired token");
      } else if (status === 400) {
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          "Invalid request";
        errorToast(msg);
      } else {
        errorToast("Failed to reset password, please try again");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-gradient-to-br from-blue-50 via-white to-purple-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200 rounded-full filter blur-3xl opacity-30 -translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200 rounded-full filter blur-3xl opacity-30 translate-x-1/2 translate-y-1/2"></div>
      
      <SeoMeta
        title="Reset Password | CodeArena"
        description="Reset your CodeArena password"
        url="https://www.code-arena.in/reset-password"
        image="/logo192.png"
      />

      <div className="relative max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-2xl">C</span>
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              CodeArena
            </span>
          </Link>
        </div>
        
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 sm:p-10">
          {/* Icon */}
          <div className="w-14 h-14 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center mb-5 shadow-lg mx-auto">
            <FaShieldAlt className="w-6 h-6 text-white" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 text-center mb-2">Reset Password</h1>
          <p className="text-center text-gray-600 mb-8">
            {token
              ? "Create a new secure password for your account."
              : "Reset link is invalid. Please request a new reset email."}
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none text-gray-900"
                  placeholder="Enter new password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                </button>
              </div>

              {/* Password Rules */}
              <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                <ul className="text-sm space-y-1.5">
                  <PasswordRule isValid={passwordRules.upperCase} text="At least one uppercase letter" />
                  <PasswordRule isValid={passwordRules.lowerCase} text="At least one lowercase letter" />
                  <PasswordRule isValid={passwordRules.number} text="At least one number" />
                  <PasswordRule isValid={passwordRules.specialChar} text="At least one special character" />
                  <PasswordRule isValid={passwordRules.minLength} text={`Minimum ${MIN_PASSWORD_LEN} characters`} />
                </ul>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm Password</label>
              <div className="relative">
                <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none text-gray-900"
                  placeholder="Confirm your password"
                />
              </div>
              {!confirmOk && (
                <p className="text-xs text-red-600 mt-2 font-medium">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-[1.02]"
            >
              <FaShieldAlt className="w-4 h-4" />
              {isSubmitting ? "Updating..." : "Update Password"}
            </button>
          </form>

          <div className="text-center mt-6">
            <Link to="/login" className="text-blue-600 hover:text-blue-700 font-semibold text-sm transition-colors">
              ← Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function PasswordRule({ isValid, text }) {
  return (
    <li className={`flex items-center gap-2 transition-colors ${isValid ? "text-green-600" : "text-gray-400"}`}>
      {isValid ? <FaCheckCircle className="w-3.5 h-3.5" /> : <FaTimesCircle className="w-3.5 h-3.5" />}
      <span className="text-xs">{text}</span>
    </li>
  );
}
